import { useState, useEffect, useCallback } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    functionality: false,
    targeting: false
  });

  const checkAndShow = useCallback(() => {
    // Check if age is verified first
    const ageVerified = localStorage.getItem('ageVerified');
    if (!ageVerified) {
      // Don't show cookie consent until age is verified
      setIsVisible(false);
      return;
    }

    // Check if user has already set cookie preferences
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setIsVisible(true);
    } else {
      // Load saved preferences
      const savedPreferences = JSON.parse(localStorage.getItem('cookiePreferences') || '{}');
      if (Object.keys(savedPreferences).length > 0) {
        setCookiePreferences(prev => ({ ...prev, ...savedPreferences }));
      }
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    checkAndShow();

    // Listen for age verification event
    const handleAgeVerified = () => {
      checkAndShow();
    };

    // Listen for storage changes (cross-tab updates)
    const handleStorageChange = (e) => {
      if (e.key === 'ageVerified' && e.newValue === 'true') {
        checkAndShow();
      }
    };

    window.addEventListener('ageVerified', handleAgeVerified);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('ageVerified', handleAgeVerified);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAndShow]);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functionality: true,
      targeting: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      functionality: false,
      targeting: false
    };
    setCookiePreferences(onlyEssential);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyEssential));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setIsVisible(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    setCookiePreferences({
      ...cookiePreferences,
      [key]: !cookiePreferences[key]
    });
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md transform transition-all duration-300">
      <div className="glass-strong rounded-2xl p-6 shadow-2xl border border-white/30">
        {!showSettings ? (
          <>
            <div className="flex items-start space-x-3 mb-4">
              <Cookie className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-2">Cookie Preferences</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or learn more in our{' '}
                  <Link to="/cookie-policy" className="text-green-600 hover:underline" data-discover="true">
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 bg-gradient-to-r from-pink-600 to-green-600 text-white text-sm font-semibold rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Accept All
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 bg-white/50 text-gray-700 text-sm font-semibold rounded-lg hover:bg-white/70 transition-colors flex items-center justify-center space-x-1"
              >
                <Settings className="w-4 h-4" />
                <span>Customize</span>
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Reject All
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Cookie Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Essential Cookies</p>
                  <p className="text-xs text-gray-600">Required for the website to function</p>
                </div>
                <div className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                  Always Active
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Analytics Cookies</p>
                  <p className="text-xs text-gray-600">Help us understand website usage</p>
                </div>
                <button
                  onClick={() => togglePreference('analytics')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    cookiePreferences.analytics ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                      cookiePreferences.analytics ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Functionality Cookies</p>
                  <p className="text-xs text-gray-600">Enable enhanced features</p>
                </div>
                <button
                  onClick={() => togglePreference('functionality')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    cookiePreferences.functionality ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                      cookiePreferences.functionality ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Targeting Cookies</p>
                  <p className="text-xs text-gray-600">Used for advertising purposes</p>
                </div>
                <button
                  onClick={() => togglePreference('targeting')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    cookiePreferences.targeting ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                      cookiePreferences.targeting ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-600 to-green-600 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
              >
                Save Preferences
              </button>
              <Link
                to="/cookie-policy"
                className="px-4 py-2 bg-white/50 text-gray-700 text-sm font-semibold rounded-lg hover:bg-white/70 transition-colors flex items-center justify-center"
                data-discover="true"
              >
                Learn More
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;

